import { useCallback, useState } from 'react';

import type { FilterOption } from './FilterBar';

export type FilterBarChangeMeta = {
  selectedLabel?: { filterId: string; label: string };
  /** true = todos los ítems del catálogo (sin listar IDs); false = quitar ese modo */
  selectAll?: boolean;
  filterId?: string;
};

/** Select-all habilitado en múltiples salvo catálogos remotos grandes o `enableSelectAll: false`. */
export function isFilterSelectAllEnabled(filter: FilterOption): boolean {
  if (filter.multiple !== true) return false;
  if (filter.enableSelectAll === false) return false;
  if (filter.enableSelectAll === true) return true;
  return !filter.loadOptionsRemotely;
}

export function applyFilterChangeSelectAll(
  prev: Record<string, boolean>,
  changedFilters: Record<string, string[]>,
  meta?: FilterBarChangeMeta,
): Record<string, boolean> {
  const next = { ...prev };

  if (meta?.filterId !== undefined && meta.selectAll !== undefined) {
    if (meta.selectAll) {
      next[meta.filterId] = true;
    } else {
      delete next[meta.filterId];
    }
  }

  for (const [filterId, values] of Object.entries(changedFilters)) {
    if (values.length > 0) {
      delete next[filterId];
    }
  }

  return next;
}

export function useFilterBarSelectAll() {
  const [selectAllByFilter, setSelectAllByFilter] = useState<Record<string, boolean>>({});

  const updateSelectAllFromChange = useCallback(
    (changedFilters: Record<string, string[]>, meta?: FilterBarChangeMeta) => {
      setSelectAllByFilter((prev) => applyFilterChangeSelectAll(prev, changedFilters, meta));
    },
    [],
  );

  const clearSelectAllFor = useCallback((filterId: string) => {
    setSelectAllByFilter((prev) => {
      if (!prev[filterId]) return prev;
      const next = { ...prev };
      delete next[filterId];
      return next;
    });
  }, []);

  const clearAllSelectAll = useCallback(() => {
    setSelectAllByFilter({});
  }, []);

  return {
    selectAllByFilter,
    updateSelectAllFromChange,
    clearSelectAllFor,
    clearAllSelectAll,
  };
}
